import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, AreaChart, Area, BarChart, Bar, ReferenceLine } from 'recharts';
import { FormControl, Select, MenuItem, Button } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import ProductSelector from './ProductSelector';
import MotivationPostsDisplay from './MotivationPostsDisplay';
import { apiPost } from '../services/apiClient';

const MOTIVATION_TYPES = [
    { value: 'satisfaction_reason', label: 'Satisfaction Reasons' },
    { value: 'dissatisfaction_reason', label: 'Dissatisfaction Reasons' },
    { value: 'joining', label: 'Joining Reasons' },
    { value: 'leaving', label: 'Leaving Reasons' },
    { value: 'usage_reason', label: 'Usage Reasons' }
];

const TIME_RANGES = [
    { value: '1M', label: '1 Month', months: 1 },
    { value: '6W', label: '6 Weeks', days: 40 },
    { value: '3M', label: '3 Months', months: 3 },
    { value: '6M', label: '6 Months', months: 6 },
    { value: '1Y', label: '1 Year', months: 12 },
    { value: '2Y', label: '2 Years', months: 24 },
    { value: '3Y', label: '3 Years', months: 36 },
    { value: '5Y', label: '5 Years', months: 60 }
];

// Colorblind-friendly palette with high contrast
// First 5 are optimized for maximum distinction
const COLORS = [
    '#2C3E50', // Charcoal (BEST 1) - professional dark gray-blue
    '#E67E22', // Burnt orange (BEST 2) - warm, distinct
    '#3498DB', // Sky blue (BEST 3) - clear, professional
    '#27AE60', // Emerald green (BEST 4) - natural, distinct
    '#8E44AD', // Royal purple (BEST 5) - unique, professional
    '#9E9E9E', // Medium gray
    '#B39DDB', // Light purple
    '#512DA8', // Dark purple
    '#FFB74D', // Light orange
    '#E65100', // Dark orange
    '#A1887F', // Light brown
    '#3E2723'  // Dark brown
];

// SIMPLE R-squared calculation - FIXED VERSION
const calculateRSquared = (data, valueKey) => {
    console.log(`\n🔍 FIXED R² CALCULATION FOR: ${valueKey}`);

    // Get y values
    const yValues = [];
    for (let i = 0; i < data.length; i++) {
        const value = data[i][valueKey];
        if (value != null && !isNaN(value) && isFinite(value)) {
            yValues.push(parseFloat(value));
        }
    }

    if (yValues.length < 3) return { rSquared: 0, slope: 0, intercept: 0 };

    const n = yValues.length;
    console.log(`Using ${n} data points`);

    // Calculate correlation coefficient first (simpler approach)
    const xValues = Array.from({length: n}, (_, i) => i);

    // Calculate means
    const xMean = xValues.reduce((sum, x) => sum + x, 0) / n;
    const yMean = yValues.reduce((sum, y) => sum + y, 0) / n;

    console.log(`Data range: ${yValues[0].toFixed(2)} to ${yValues[n-1].toFixed(2)}`);
    console.log(`Mean: ${yMean.toFixed(3)}`);

    // Check if we're getting the right data by showing every 50th point
    console.log(`Every 50th point:`);
    for (let i = 0; i < n; i += 50) {
        console.log(`  Point ${i}: ${yValues[i].toFixed(3)}`);
    }

    // Calculate correlation coefficient
    let numerator = 0;
    let sumXSquared = 0;
    let sumYSquared = 0;

    for (let i = 0; i < n; i++) {
        const xDiff = xValues[i] - xMean;
        const yDiff = yValues[i] - yMean;
        numerator += xDiff * yDiff;
        sumXSquared += xDiff * xDiff;
        sumYSquared += yDiff * yDiff;
    }

    const correlation = numerator / Math.sqrt(sumXSquared * sumYSquared);
    const rSquared = correlation * correlation;

    // Calculate slope for trend line
    const slope = numerator / sumXSquared;
    const intercept = yMean - slope * xMean;

    console.log(`Correlation: ${correlation.toFixed(6)}`);
    console.log(`R²: ${rSquared.toFixed(6)}`);
    console.log(`Slope: ${slope.toFixed(6)}`);
    console.log(`Trend line: ${intercept.toFixed(3)} to ${(slope * (n-1) + intercept).toFixed(3)}`);

    return { rSquared, slope, intercept };
};

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        // Filter out trend lines from tooltip
        const filteredPayload = payload.filter(entry => !entry.dataKey?.includes('_trend'));
        if (filteredPayload.length === 0) return null;

        return (
            <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                padding: '10px 12px',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }}>
                <p style={{
                    margin: '0 0 6px',
                    fontWeight: 500,
                    fontSize: '11px',
                    color: '#6B7280',
                    letterSpacing: '0.02em'
                }}>
                    {label}
                </p>
                {filteredPayload.map((entry, index) => (
                    <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        margin: '3px 0',
                        fontSize: '12px'
                    }}>
                        <span style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            backgroundColor: entry.color,
                            marginRight: '6px',
                            flexShrink: 0
                        }} />
                        <span style={{ color: '#374151', fontWeight: 500 }}>
                            {entry.name}:
                        </span>
                        <span style={{ marginLeft: '4px', color: '#1a1a1a', fontWeight: 600 }}>
                            {entry.value.toFixed(1)}%
                        </span>
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

const SatisfactionReasons = ({ topicMap, companyName,
    // Optional controlled product selection, so a parent (e.g. the page-
    // wide sticky topic selector) can share the selected topic across
    // sections. Falls back to internal state when omitted.
    selectedProduct: controlledSelectedProduct, onProductChange }) => {
    const [internalSelectedProduct, setInternalSelectedProduct] = useState(null);
    const isProductControlled = controlledSelectedProduct !== undefined && typeof onProductChange === 'function';
    const selectedProduct = isProductControlled ? controlledSelectedProduct : internalSelectedProduct;
    const setSelectedProduct = isProductControlled ? onProductChange : setInternalSelectedProduct;
    const [selectedType, setSelectedType] = useState('satisfaction_reason');
    const [timeRange, setTimeRange] = useState('1Y');
    const [rollingDays, setRollingDays] = useState(3);
    const [rollingWindowDays, setRollingWindowDays] = useState(90); // For % share calculation
    const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
    const [chartData, setChartData] = useState([]);
    const [stackedChartData, setStackedChartData] = useState([]);
    const [countChartData, setCountChartData] = useState([]);
    const [topValues, setTopValues] = useState([]);
    const [allPossibleValues, setAllPossibleValues] = useState([]);
    const [valueColorMapping, setValueColorMapping] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showTop5Only, setShowTop5Only] = useState(false);
    const [visibleLines, setVisibleLines] = useState(new Set()); // Track which lines are visible
    const [maxYValue, setMaxYValue] = useState(0); // Store max Y value for consistent scale
    const [maxCountValue, setMaxCountValue] = useState(0); // Store max count value for consistent scale
    const [rSquaredValues, setRSquaredValues] = useState({}); // Store R² values for each line
    const [regressionParams, setRegressionParams] = useState({}); // Store regression parameters for trend lines
    const [totalPostCount, setTotalPostCount] = useState(0); // Store total posts analyzed
    const [showTrendLines, setShowTrendLines] = useState(false); // Toggle for showing trend lines
    const [chartView, setChartView] = useState('trend'); // 'trend', 'proportional', or 'count'

    // Posts display state
    const [postsReferenced, setPostsReferenced] = useState([]);
    const [postsLoading, setPostsLoading] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState(null);

    // Dynamic motivation types from API
    const [motivationTypes, setMotivationTypes] = useState([]);
    const [typesLoading, setTypesLoading] = useState(false);

    // Filter topicMap to only include product topics
    const filteredTopicMap = useMemo(() => {
        if (!topicMap) return {};
        
        const filtered = {};
        Object.keys(topicMap).forEach(id => {
            const topic = topicMap[id];
            if (topic) {
                filtered[id] = topic;
            }
        });

        // Auto-select first available product if none selected
        if (Object.keys(filtered).length > 0 && !selectedProduct) {
            const sortedItems = Object.values(filtered)
                .sort((a, b) => {
                    const priorityA = a.priority === undefined ? Infinity : a.priority;
                    const priorityB = b.priority === undefined ? Infinity : b.priority;
                    return priorityA - priorityB;
                });
            
            if (sortedItems[0]) {
                setSelectedProduct(sortedItems[0].id);
            }
        }
        
        return filtered;
    }, [topicMap, selectedProduct]);

    // Calculate date range when timeRange changes
    useEffect(() => {
        const now = new Date();
        const endDate = new Date(now);
        const displayStartDate = new Date(now);
        const selectedRange = TIME_RANGES.find(r => r.value === timeRange);
        
        // Safety check - if no range found, default to 1 year
        if (!selectedRange) {
            console.error('Invalid timeRange:', timeRange, 'defaulting to 1Y');
            displayStartDate.setMonth(endDate.getMonth() - 12);
        } else if (selectedRange.days) {
            displayStartDate.setDate(endDate.getDate() - selectedRange.days);
        } else {
            displayStartDate.setMonth(endDate.getMonth() - selectedRange.months);
        }
        
        // For rolling averages, fetch data starting rollingWindowDays before display range
        const dataStartDate = new Date(displayStartDate);
        dataStartDate.setDate(dataStartDate.getDate() - rollingWindowDays);
        
        setDateRange({ 
            startDate: dataStartDate,  // Fetch extra rollingWindowDays of data
            endDate: endDate,
            displayStartDate: displayStartDate  // Where to start showing buckets
        });
    }, [timeRange, rollingWindowDays]);

    // Fetch data when parameters change
    useEffect(() => {
        if (!selectedProduct || !dateRange.startDate || !dateRange.endDate) {
            setChartData([]);
            setStackedChartData([]);
            setCountChartData([]);
            setTopValues([]);
            setAllPossibleValues([]);
            setValueColorMapping({});
            return;
        }

        const fetchData = async () => {
            setIsLoading(true);
            setError(null);

            try {

                console.log('FETCHING DATA WITH FIXED DATE RANGE:', {
                    topicId: selectedProduct,
                    type: selectedType,
                    startDate: dateRange.startDate.toISOString(),
                    endDate: dateRange.endDate.toISOString(),
                    timeRange: timeRange
                });

                const response = await apiPost('topic/motivation/histogram-rolling', {
                    topic_id: selectedProduct,
                    start_time: dateRange.startDate.toISOString(),
                    end_time: dateRange.endDate.toISOString(),
                    motivation_type: selectedType,
                });

                if (!response.ok) {
                    throw new Error(`Backend error: ${response.status}`);
                }

                const json = await response.json();
                const data = json.data ?? json ?? [];

                console.log('Received motivation data:', data?.length || 0, 'records');
                console.log('Sample data:', data?.slice(0, 3));

                if (!data || data.length === 0) {
                    console.log('No data returned for parameters:', {
                        topicId: selectedProduct,
                        type: selectedType,
                        startDate: dateRange.startDate.toISOString(),
                        endDate: dateRange.endDate.toISOString()
                    });
                    setChartData([]);
                    setStackedChartData([]);
                    setCountChartData([]);
                    setTopValues([]);
                    setAllPossibleValues([]);
                    setValueColorMapping({});
                    return;
                }

                // Get all unique values and their total counts
                const valueCounts = {};
                data.forEach(row => {
                    valueCounts[row.value] = (valueCounts[row.value] || 0) + parseFloat(row.count);
                });
                
                // Sort by frequency (highest first) to assign best colors to most frequent
                const sortedByFrequency = Object.entries(valueCounts)
                    .sort(([,a], [,b]) => b - a)
                    .map(([value]) => value);
                
                console.log('Values sorted by frequency:', sortedByFrequency.slice(0, 10));
                
                // Create color mapping - most frequent get the best colors
                const colorMapping = {};
                sortedByFrequency.forEach((value, index) => {
                    colorMapping[value] = COLORS[index % COLORS.length];
                });
                
                setAllPossibleValues(sortedByFrequency);
                setValueColorMapping(colorMapping);

                // Process data for charting
                processChartData(data);
                
            } catch (error) {
                console.error('Error fetching motivation data:', error);
                setError(error.message);
                setChartData([]);
                setStackedChartData([]);
                setCountChartData([]);
                setTopValues([]);
                setAllPossibleValues([]);
                setValueColorMapping({});
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [selectedProduct, selectedType, dateRange.startDate, dateRange.endDate, rollingDays, rollingWindowDays, showTop5Only]);

    const processChartData = (rawData) => {
        // Get all unique values and their total counts
        const valueCounts = {};
        rawData.forEach(row => {
            valueCounts[row.value] = (valueCounts[row.value] || 0) + parseFloat(row.count);
        });

        // Sort by frequency for color assignment
        const sortedByFrequency = Object.entries(valueCounts)
            .sort(([,a], [,b]) => b - a)
            .map(([value]) => value);

        // Filter out "other" and "unknown" for top values
        const filteredValues = sortedByFrequency.filter(value =>
            !value.toLowerCase().includes('other') &&
            !value.toLowerCase().includes('unknown')
        );
        const topN = showTop5Only ? 5 : 10;
        const topValues = filteredValues.slice(0, topN);

        // Create color mapping - assign best colors to filtered top values
        const colorMapping = {};
        // First assign best colors to our filtered top values
        topValues.forEach((value, index) => {
            colorMapping[value] = COLORS[index % COLORS.length];
        });
        // Then assign remaining colors to other values (including other/unknown)
        let colorIndex = topValues.length;
        sortedByFrequency.forEach(value => {
            if (!colorMapping[value]) {
                colorMapping[value] = COLORS[colorIndex % COLORS.length];
                colorIndex++;
            }
        });

        setAllPossibleValues(sortedByFrequency);
        setValueColorMapping(colorMapping);
        setTopValues(topValues);

        // Reset visible lines
        if (visibleLines.size === 0) {
            setVisibleLines(new Set());
        }

        // Find the last actual data point
        let lastDataTime = 0;
        rawData.forEach(row => {
            // Parse date with time set to end of day to avoid timezone issues
            const rowDate = new Date(row.post_date);
            rowDate.setHours(23, 59, 59, 999); // Set to end of day
            const rowTime = rowDate.getTime();
            if (rowTime > lastDataTime) {
                lastDataTime = rowTime;
            }
        });

        console.log('Last data point:', new Date(lastDataTime), 'vs requested end time:', dateRange.endDate);

        // Create buckets at specified intervals - only for the display range
        const buckets = [];
        const bucketSize = rollingDays * 24 * 60 * 60 * 1000; // Convert days to milliseconds
        const displayStartTime = dateRange.displayStartDate.getTime();
        // Extend to the requested end date, or last data point + 1 day buffer
        const endDateWithBuffer = new Date(dateRange.endDate);
        endDateWithBuffer.setDate(endDateWithBuffer.getDate() + 1);
        const endTime = Math.max(lastDataTime, endDateWithBuffer.getTime());

        // Create bucket dates for display range only up to last data point
        for (let time = displayStartTime; time <= endTime; time += bucketSize) {
            buckets.push({
                date: new Date(time),
                time: time,
                values: {},
                counts: {}
            });
        }
        
        // For each bucket, calculate rolling sums
        buckets.forEach(bucket => {
            const bucketDate = bucket.date;
            const windowStartDate = new Date(bucketDate);
            windowStartDate.setDate(bucketDate.getDate() - rollingWindowDays);
            
            // Sum all theme posts and total posts in the rolling window
            const themeSums = {};
            let totalSum = 0;
            
            rawData.forEach(row => {
                const rowDate = new Date(row.post_date);
                if (rowDate > windowStartDate && rowDate <= bucketDate) {
                    const value = row.value;
                    const count = parseFloat(row.count);
                    
                    themeSums[value] = (themeSums[value] || 0) + count;
                    totalSum += count;
                }
            });
            
            // Calculate percentages: (rolling window theme sum / rolling window total sum) * 100
            topValues.forEach(value => {
                const themeSum = themeSums[value] || 0;
                bucket.values[value] = totalSum > 0 ? (themeSum / totalSum) * 100 : 0;
                bucket.counts[value] = themeSum;
            });
        });
        
        // Convert to chart format
        const chartData = buckets.map(bucket => {
            // For time ranges longer than a year, include year in date formatting
            const timeRangeMonths = TIME_RANGES.find(r => r.value === timeRange)?.months || 12;
            const isLongTimeRange = timeRangeMonths > 12;
            
            let formattedDate;
            if (rollingDays === 30 && timeRangeMonths > 12) {
                // For monthly buckets beyond 1 year, show just year
                formattedDate = bucket.date.toLocaleDateString('en-US', { year: 'numeric' });
            } else if (rollingDays === 30) {
                // For monthly buckets within 1 year, show month and year
                formattedDate = bucket.date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
            } else if (isLongTimeRange) {
                // For other long time ranges, show year only
                formattedDate = bucket.date.toLocaleDateString('en-US', { year: '2-digit' });
            } else {
                // For short time ranges, show month and day
                formattedDate = bucket.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            }
            
            const dataPoint = { 
                date: formattedDate,
                fullDate: bucket.date // Keep full date for reference lines
            };
            topValues.forEach(value => {
                dataPoint[value] = bucket.values[value];
            });
            return dataPoint;
        });
        
        // Stacked data (normalize first two charts to relative percentages)
        const stackedData = chartData.map(periodData => {
            const normalized = { 
                date: periodData.date,
                fullDate: periodData.fullDate 
            };
            let totalForPeriod = 0;
            
            topValues.forEach(value => {
                totalForPeriod += periodData[value] || 0;
            });
            
            if (totalForPeriod > 0) {
                topValues.forEach(value => {
                    normalized[value] = (periodData[value] || 0) / totalForPeriod * 100;
                });
            } else {
                topValues.forEach(value => {
                    normalized[value] = 0;
                });
            }
            
            return normalized;
        });
        
        // Count data - for the third chart, bucket by time period (no rolling)
        // Use lastDataTime which is the last actual data point
        const countData = buckets.map(bucket => {
            const bucketDate = bucket.date;
            const bucketSizeMs = rollingDays * 24 * 60 * 60 * 1000;
            const bucketStart = bucketDate.getTime();
            const bucketEnd = Math.min(bucketStart + bucketSizeMs, lastDataTime);
            
            // Use same date formatting logic as main chart data
            const timeRangeMonths = TIME_RANGES.find(r => r.value === timeRange)?.months || 12;
            const isLongTimeRange = timeRangeMonths > 12;
            
            let formattedDate;
            if (rollingDays === 30 && timeRangeMonths > 12) {
                // For monthly buckets beyond 1 year, show just year
                formattedDate = bucket.date.toLocaleDateString('en-US', { year: 'numeric' });
            } else if (rollingDays === 30) {
                // For monthly buckets within 1 year, show month and year
                formattedDate = bucket.date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
            } else if (isLongTimeRange) {
                // For other long time ranges, show year only
                formattedDate = bucket.date.toLocaleDateString('en-US', { year: '2-digit' });
            } else {
                // For short time ranges, show month and day
                formattedDate = bucket.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            }
            
            // Sum counts for this time bucket only
            const bucketCounts = {};
            rawData.forEach(row => {
                const rowTime = new Date(row.post_date).getTime();
                if (rowTime >= bucketStart && rowTime < bucketEnd) {
                    const value = row.value;
                    const count = parseFloat(row.count);
                    bucketCounts[value] = (bucketCounts[value] || 0) + count;
                }
            });
            
            const countPoint = { 
                date: formattedDate,
                fullDate: bucket.date 
            };
            topValues.forEach(value => {
                countPoint[value] = bucketCounts[value] || 0;
            });
            return countPoint;
        });
        
        // Calculate max values for scaling
        let maxValue = 0;
        let maxCountVal = 0;
        
        chartData.forEach(dataPoint => {
            topValues.forEach(value => {
                maxValue = Math.max(maxValue, dataPoint[value] || 0);
            });
        });
        
        countData.forEach(dataPoint => {
            topValues.forEach(value => {
                maxCountVal = Math.max(maxCountVal, dataPoint[value] || 0);
            });
        });
        
        setMaxYValue(Math.ceil(maxValue * 1.1));
        setMaxCountValue(Math.ceil(maxCountVal * 1.1));

        // Just calculate trend lines without R²
        const rSquaredMap = {};
        const regressionMap = {};

        topValues.forEach(value => {
            const y = chartData.map(d => d[value]).filter(v => v != null && !isNaN(v));

            if (y.length < 10) {
                rSquaredMap[value] = null;
                regressionMap[value] = { slope: 0, intercept: 0 };
                return;
            }

            const n = y.length;
            const firstVal = y[0];
            const lastVal = y[n - 1];

            // Simple slope and intercept
            const slope = (lastVal - firstVal) / (n - 1);
            const intercept = firstVal;

            rSquaredMap[value] = null; // No R² values
            regressionMap[value] = { slope, intercept };
        });

        setRSquaredValues(rSquaredMap);
        setRegressionParams(regressionMap);

        // Calculate total unique post count in the time range
        // Group by post_date and sum total_posts once per date to avoid double-counting
        const datePostCounts = {};
        rawData.forEach(row => {
            if (row.total_posts && !datePostCounts[row.post_date]) {
                datePostCounts[row.post_date] = parseFloat(row.total_posts);
            }
        });
        const totalUniquePosts = Object.values(datePostCounts).reduce((sum, count) => sum + count, 0);
        setTotalPostCount(totalUniquePosts);
        console.log('Unique posts calculated:', totalUniquePosts, 'from', Object.keys(datePostCounts).length, 'unique dates');

        // Add trend line data points for each series
        const chartDataWithTrends = chartData.map((point, index) => {
            const trendPoint = { ...point };
            topValues.forEach(value => {
                if (regressionMap[value]) {
                    const { slope, intercept } = regressionMap[value];
                    // Calculate the trend line value for this x position
                    const trendValue = slope * index + intercept;
                    trendPoint[`${value}_trend`] = trendValue;

                    // Log some trend line values for debugging
                    if (index === 0 || index === chartData.length - 1) {
                        console.log(`${value} trend at x=${index}: actual=${point[value]?.toFixed(2)}, trend=${trendValue.toFixed(2)}`);
                    }
                }
            });
            return trendPoint;
        });

        // Trim data to only include points with actual data (not trailing zeros)
        // Find first index with actual data
        let firstDataIndex = 0;
        for (let i = 0; i < chartDataWithTrends.length; i++) {
            const hasData = topValues.some(value => chartDataWithTrends[i][value] > 0);
            if (hasData) {
                firstDataIndex = i;
                break;
            }
        }

        // Find last index with actual data
        let lastDataIndex = chartDataWithTrends.length - 1;
        for (let i = chartDataWithTrends.length - 1; i >= 0; i--) {
            const hasData = topValues.some(value => chartDataWithTrends[i][value] > 0);
            if (hasData) {
                lastDataIndex = i;
                break;
            }
        }

        // Trim all chart data arrays to only include actual data range
        const trimmedChartData = chartDataWithTrends.slice(firstDataIndex, lastDataIndex + 1);
        const trimmedStackedData = stackedData.slice(firstDataIndex, lastDataIndex + 1);
        const trimmedCountData = countData.slice(firstDataIndex, lastDataIndex + 1);

        console.log('Data trimming:', {
            originalLength: chartDataWithTrends.length,
            firstDataIndex,
            lastDataIndex,
            trimmedLength: trimmedChartData.length
        });

        setChartData(trimmedChartData);
        setStackedChartData(trimmedStackedData);
        setCountChartData(trimmedCountData);

        console.log('12-month rolling bucket data:', {
            buckets: trimmedChartData.length,
            topValues: topValues.slice(0, 5),
            sampleBucket: trimmedChartData[0],
            rSquaredValues: rSquaredMap,
            regressionParams: regressionMap
        });
    };

    // Handle legend clicks to toggle line visibility
    const handleLegendClick = (value) => {
        const newVisibleLines = new Set(visibleLines);
        
        if (newVisibleLines.has(value)) {
            // Remove the line if it's currently visible
            newVisibleLines.delete(value);
        } else {
            // Add the line if it's not currently visible
            newVisibleLines.add(value);
        }
        
        setVisibleLines(newVisibleLines);
    };

    // Determine which lines should be shown
    const shouldShowLine = (value) => {
        // If no lines are specifically selected, show all
        if (visibleLines.size === 0) return true;
        // Otherwise, only show selected lines
        return visibleLines.has(value);
    };

    // Handle chart click to fetch posts for the clicked period
    const handleChartClick = useCallback(async (clickData) => {
        if (!selectedProduct) return;

        // Get the clicked data point
        const { activePayload, activeLabel } = clickData || {};
        if (!activePayload || activePayload.length === 0) return;

        // Find the fullDate from the data point
        const dataPoint = chartData.find(d => d.date === activeLabel);
        if (!dataPoint || !dataPoint.fullDate) return;

        const endDate = new Date(dataPoint.fullDate);
        const startDate = new Date(dataPoint.fullDate);
        startDate.setDate(startDate.getDate() - rollingDays);

        const periodLabel = `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

        setPostsLoading(true);
        setSelectedPeriod(periodLabel);

        try {
            // Get the selected value filter (if user has filtered to specific themes)
            const selectedValues = visibleLines.size > 0 ? Array.from(visibleLines) : null;

            const requestBody = {
                topic_id: selectedProduct,
                start_date: startDate.toISOString(),
                end_date: endDate.toISOString(),
                motivation_type: selectedType,
                values: selectedValues,
            };

            console.log('=== SatisfactionReasons: API Request ===');
            console.log('Endpoint:', 'topic/motivation/posts');
            console.log('Request body:', requestBody);

            const response = await apiPost('topic/motivation/posts', requestBody);

            if (!response.ok) {
                throw new Error(`Failed to fetch posts: ${response.status}`);
            }

            const posts = await response.json();

            console.log('=== SatisfactionReasons: API Response ===');
            console.log('Posts received:', posts.length);
            console.log('First post:', posts[0]);
            console.log('Post keys:', posts[0] ? Object.keys(posts[0]) : 'no posts');

            setPostsReferenced(posts);
        } catch (err) {
            console.error('Error fetching motivation posts:', err);
            setPostsReferenced([]);
        } finally {
            setPostsLoading(false);
        }
    }, [selectedProduct, selectedType, rollingDays, chartData, visibleLines]);

    // Close posts display
    const handleClosePosts = useCallback(() => {
        setPostsReferenced([]);
        setSelectedPeriod(null);
    }, []);

    // Clear posts and reset visible lines when filters change
    useEffect(() => {
        setPostsReferenced([]);
        setSelectedPeriod(null);
        setVisibleLines(new Set());
    }, [selectedProduct, selectedType, timeRange]);

    // Fetch motivation types when product changes
    useEffect(() => {
        const fetchMotivationTypes = async () => {
            if (!selectedProduct) {
                setMotivationTypes([]);
                return;
            }

            setTypesLoading(true);
            try {
                const response = await apiPost('topic/motivation/types', { topic_id: selectedProduct });

                if (!response.ok) {
                    throw new Error(`Failed to fetch types: ${response.status}`);
                }

                const types = await response.json();
                setMotivationTypes(types);
                // Set first type as default if available and current selection not in list
                if (types.length > 0 && !types.find(t => t.value === selectedType)) {
                    setSelectedType(types[0].value);
                }
            } catch (err) {
                console.error('Error fetching motivation types:', err);
                setMotivationTypes([]);
            } finally {
                setTypesLoading(false);
            }
        };

        fetchMotivationTypes();
    }, [selectedProduct]);

    // Helper function to compute label y-offsets with collision avoidance
    const computeLabelOffsets = useMemo(() => {
        if (!chartData.length || !topValues.length) return {};

        const lastPoint = chartData[chartData.length - 1];
        const MIN_SPACING = 14; // Minimum pixels between label centers

        // Get y-values for all visible lines at the last data point
        const labelData = topValues
            .filter(value => shouldShowLine(value))
            .map(value => ({
                value,
                yValue: lastPoint[value] || 0
            }))
            .sort((a, b) => b.yValue - a.yValue); // Sort by y-value (highest first)

        // Compute offsets to avoid collisions
        const offsets = {};
        const usedPositions = [];

        labelData.forEach((item) => {
            // Estimate pixel y based on relative position (higher value = lower y in SVG)
            const estimatedY = (1 - item.yValue / (maxYValue || 100)) * 380;

            let offset = 0;
            let finalY = estimatedY;

            // Check for collisions with already-placed labels
            for (const usedY of usedPositions) {
                if (Math.abs(finalY - usedY) < MIN_SPACING) {
                    // Push this label down to avoid collision
                    finalY = usedY + MIN_SPACING;
                    offset = finalY - estimatedY;
                }
            }

            offsets[item.value] = offset;
            usedPositions.push(finalY);
        });

        return offsets;
    }, [chartData, topValues, maxYValue, visibleLines]);

    // Helper function to get evenly spaced tick values (max 8)
    const getEvenlySpacedTicks = (data, maxTicks = 8) => {
        if (!data || data.length === 0) return [];
        if (data.length <= maxTicks) return data.map(d => d.date);

        const ticks = [];
        const step = (data.length - 1) / (maxTicks - 1);

        for (let i = 0; i < maxTicks; i++) {
            const index = Math.round(i * step);
            ticks.push(data[index].date);
        }

        return ticks;
    };

    // Helper function to format date as "Mon YYYY"
    const formatTickDate = (value, data) => {
        const point = data.find(d => d.date === value);
        if (point && point.fullDate) {
            return point.fullDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        }
        return value;
    };

    // Helper function to create year reference lines for long time ranges
    const createYearReferenceLines = (data) => {
        const timeRangeMonths = TIME_RANGES.find(r => r.value === timeRange)?.months || 12;
        console.log('createYearReferenceLines:', { timeRange, timeRangeMonths, dataLength: data?.length });
        
        if (timeRangeMonths <= 12 || !data || data.length === 0) return [];
        
        const yearLines = [];
        const processedYears = new Set();
        
        // Go through data points and find year transitions
        for (let i = 1; i < data.length; i++) {
            const currentPoint = data[i];
            const previousPoint = data[i - 1];
            
            if (currentPoint.fullDate && previousPoint.fullDate) {
                const currentYear = currentPoint.fullDate.getFullYear();
                const previousYear = previousPoint.fullDate.getFullYear();
                
                // If we've moved to a new year and haven't processed this year yet
                if (currentYear > previousYear && !processedYears.has(currentYear)) {
                    yearLines.push({
                        year: currentYear,
                        dataKey: currentPoint.date
                    });
                    processedYears.add(currentYear);
                    console.log('Added year line:', currentYear, currentPoint.date);
                }
            }
        }
        
        console.log('Year lines created:', yearLines);
        return yearLines;
    };

    const getSelectedProductName = () => {
        if (selectedProduct && topicMap && topicMap[selectedProduct]) {
            return topicMap[selectedProduct].name;
        }
        return 'Product';
    };

    const getMotivationTypeLabel = () => {
        const type = MOTIVATION_TYPES.find(t => t.value === selectedType);
        return type ? type.label : 'Motivation';
    };

    const getTimeRangeLabel = () => {
        const range = TIME_RANGES.find(r => r.value === timeRange);
        return range ? range.label : timeRange;
    };

    // Build dynamic H1 title (Product · Motivation Type)
    const buildMainTitle = () => {
        return `${getSelectedProductName()} · ${getMotivationTypeLabel()}`;
    };

    // Build subline (Time Range • Rolling Window)
    const buildSubline = () => {
        return `${getTimeRangeLabel()} • ${rollingWindowDays}-Day Rolling Window`;
    };

    // Hover state for header card
    const [isHeaderHovered, setIsHeaderHovered] = useState(false);

    // Consistent control height for all dropdowns and button
    const CONTROL_HEIGHT = 36;
    const CONTROL_GAP = 12;

    // Shared styles for filter controls
    const filterLabelStyle = {
        fontSize: '9px',
        fontWeight: 600,
        color: '#999',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '3px'
    };

    const selectStyle = {
        width: '100%',
        height: CONTROL_HEIGHT,
        '& .MuiSelect-select': {
            paddingTop: '6px',
            paddingBottom: '6px',
            paddingRight: '24px !important',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d0d0d0',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#999',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1976d2',
        }
    };

    const toggleButtonStyle = (isActive) => ({
        padding: '6px 12px',
        backgroundColor: isActive ? '#1976d2' : '#fff',
        color: isActive ? 'white' : '#666',
        border: '1px solid #d0d0d0',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: 500,
        transition: 'all 0.2s ease',
        height: CONTROL_HEIGHT,
        display: 'flex',
        alignItems: 'center'
    });

    if (!topicMap || Object.keys(filteredTopicMap).length === 0) {
        return (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                No product data available for satisfaction reasons analysis
            </div>
        );
    }

    return (
        <div style={{ padding: '24px', maxWidth: '100%', boxSizing: 'border-box' }}>
            {/* Section Label */}
            <div style={{
                fontSize: '10px',
                fontWeight: 600,
                color: '#888',
                textTransform: 'uppercase',
                letterSpacing: '1.5px',
                marginBottom: '12px'
            }}>
                SATISFACTION REASONS
            </div>

            {/* Header Card */}
            <div
                onMouseEnter={() => setIsHeaderHovered(true)}
                onMouseLeave={() => setIsHeaderHovered(false)}
                style={{
                    backgroundColor: '#fafafa',
                    borderRadius: '8px',
                    padding: '20px 24px 18px',
                    marginBottom: '20px',
                    boxShadow: isHeaderHovered
                        ? '0 4px 12px rgba(0, 0, 0, 0.08)'
                        : '0 1px 3px rgba(0, 0, 0, 0.06)',
                    border: '1px solid rgba(0, 0, 0, 0.06)',
                    transition: 'box-shadow 0.2s ease',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                }}
            >
                {/* Title Block */}
                <div style={{ marginBottom: '12px' }}>
                    {/* Company Name - lightest */}
                    <div style={{
                        fontSize: '11px',
                        color: '#777',
                        fontWeight: 500,
                        lineHeight: 1.2,
                        marginBottom: '1px'
                    }}>
                        {companyName || 'Company'}
                    </div>

                    {/* Main H1 */}
                    <h1 style={{
                        fontSize: '22px',
                        fontWeight: 700,
                        margin: 0,
                        color: '#111',
                        lineHeight: 1.25
                    }}>
                        {selectedProduct ? buildMainTitle() : 'Select a Product'}
                    </h1>

                    {/* Context Line - lighter gray */}
                    {selectedProduct && (
                        <div style={{
                            fontSize: '12px',
                            color: '#999',
                            lineHeight: 1.3,
                            marginTop: '2px'
                        }}>
                            {buildSubline()}
                        </div>
                    )}
                </div>

                {/* Filter Bar */}
                <div style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: `${CONTROL_GAP}px`,
                    flexWrap: 'wrap',
                    rowGap: '12px'
                }}>
                    {/* Product */}
                    <div style={{ flex: '1 1 auto', minWidth: 100, maxWidth: 180, boxSizing: 'border-box' }}>
                        <div style={filterLabelStyle}>Product</div>
                        <ProductSelector
                            topicMap={filteredTopicMap}
                            selectedId={selectedProduct || ''}
                            onSelect={setSelectedProduct}
                            label=""
                            size="small"
                            sx={selectStyle}
                        />
                    </div>

                    {/* Motivation Type */}
                    <div style={{ flex: '1 1 auto', minWidth: 140, maxWidth: 200 }}>
                        <div style={filterLabelStyle}>Motivation Type</div>
                        <FormControl size="small" sx={selectStyle} disabled={typesLoading || motivationTypes.length === 0}>
                            <Select
                                value={selectedType}
                                onChange={(e) => setSelectedType(e.target.value)}
                            >
                                {motivationTypes.length > 0 ? (
                                    motivationTypes.map(type => (
                                        <MenuItem key={type.value} value={type.value}>
                                            {type.label}
                                        </MenuItem>
                                    ))
                                ) : (
                                    MOTIVATION_TYPES.map(type => (
                                        <MenuItem key={type.value} value={type.value}>
                                            {type.label}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>
                    </div>

                    {/* Time Range */}
                    <div style={{ flex: '1 1 auto', minWidth: 90, maxWidth: 140 }}>
                        <div style={filterLabelStyle}>Time Range</div>
                        <FormControl size="small" sx={selectStyle}>
                            <Select
                                value={timeRange}
                                onChange={(e) => setTimeRange(e.target.value)}
                            >
                                {TIME_RANGES.map(range => (
                                    <MenuItem key={range.value} value={range.value}>
                                        {range.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    {/* Rolling Average */}
                    <div style={{ flex: '1 1 auto', minWidth: 90, maxWidth: 130 }}>
                        <div style={filterLabelStyle}>Bucket Size</div>
                        <FormControl size="small" sx={selectStyle}>
                            <Select
                                value={rollingDays}
                                onChange={(e) => setRollingDays(e.target.value)}
                            >
                                <MenuItem value={3}>3 Days</MenuItem>
                                <MenuItem value={5}>5 Days</MenuItem>
                                <MenuItem value={7}>7 Days</MenuItem>
                                <MenuItem value={14}>14 Days</MenuItem>
                                <MenuItem value={30}>Monthly</MenuItem>
                            </Select>
                        </FormControl>
                    </div>

                    {/* % Share Window */}
                    <div style={{ flex: '1 1 auto', minWidth: 90, maxWidth: 130 }}>
                        <div style={filterLabelStyle}>% Window</div>
                        <FormControl size="small" sx={selectStyle}>
                            <Select
                                value={rollingWindowDays}
                                onChange={(e) => setRollingWindowDays(e.target.value)}
                            >
                                <MenuItem value={7}>7 Days</MenuItem>
                                <MenuItem value={30}>30 Days</MenuItem>
                                <MenuItem value={90}>90 Days</MenuItem>
                                <MenuItem value={365}>365 Days</MenuItem>
                            </Select>
                        </FormControl>
                    </div>

                    {/* Chart View */}
                    <div style={{ flex: '1 1 auto', minWidth: 120, maxWidth: 160 }}>
                        <div style={filterLabelStyle}>Chart View</div>
                        <FormControl size="small" sx={selectStyle}>
                            <Select
                                value={chartView}
                                onChange={(e) => setChartView(e.target.value)}
                            >
                                <MenuItem value="trend">Trend Lines</MenuItem>
                                <MenuItem value="proportional">Proportional</MenuItem>
                                <MenuItem value="count">Count</MenuItem>
                            </Select>
                        </FormControl>
                    </div>

                    {/* Toggle buttons */}
                    <div style={{ flex: '0 0 auto' }}>
                        <div style={{ ...filterLabelStyle, visibility: 'hidden' }}>&nbsp;</div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                                onClick={() => setShowTop5Only(!showTop5Only)}
                                style={toggleButtonStyle(showTop5Only)}
                            >
                                {showTop5Only ? 'Top 5' : 'Top 10'}
                            </button>

                            {chartView === 'trend' && (
                                <button
                                    onClick={() => setShowTrendLines(!showTrendLines)}
                                    style={toggleButtonStyle(showTrendLines)}
                                >
                                    Trends
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Apply Button */}
                    <div style={{ flex: '0 0 auto' }}>
                        <div style={{ ...filterLabelStyle, visibility: 'hidden' }}>&nbsp;</div>
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{
                                height: CONTROL_HEIGHT,
                                minWidth: '72px',
                                padding: '6px 16px',
                                fontSize: '13px',
                                fontWeight: 600,
                                textTransform: 'none',
                                boxShadow: 'none',
                                transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
                                '&:hover': {
                                    boxShadow: '0 2px 6px rgba(25, 118, 210, 0.3)',
                                    backgroundColor: '#1565c0'
                                }
                            }}
                        >
                            Apply
                        </Button>
                    </div>
                </div>
            </div>

            {/* Chart Section */}
            <div style={{ marginTop: '8px' }}>
                {error && (
                <div style={{ 
                    backgroundColor: '#ffebee', 
                    color: '#c62828', 
                    padding: '12px', 
                    borderRadius: '4px',
                    marginBottom: '20px'
                }}>
                    Error: {error}
                </div>
            )}

            {isLoading ? (
                <div style={{ 
                    height: '400px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: '#666'
                }}>
                    Loading satisfaction reasons data...
                </div>
            ) : chartData.length > 0 ? (
                <div>
                    {/* Line Chart - Trend View */}
                    {chartView === 'trend' && (
                    <div style={{
                        backgroundColor: '#F7F7F9',
                        borderRadius: '10px',
                        padding: '28px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)'
                    }}>
                        {/* Title Block */}
                        <div style={{ marginBottom: '16px' }}>
                            <h3 style={{
                                fontSize: '24px',
                                fontWeight: 600,
                                color: '#1a1a1a',
                                margin: 0,
                                lineHeight: 1.2
                            }}>
                                {MOTIVATION_TYPES.find(t => t.value === selectedType)?.label || 'Motivation'} Trends
                            </h3>
                            <p style={{
                                fontSize: '13px',
                                color: '#6B7280',
                                margin: '4px 0 0 0',
                                lineHeight: 1.4
                            }}>
                                n = {totalPostCount.toLocaleString()} posts · {rollingWindowDays}-day rolling % share
                            </p>
                        </div>

                        {/* Legend - Compact tag list style */}
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '12px 16px',
                            marginBottom: '16px'
                        }}>
                            {topValues.filter(value => !value.includes('_trend')).map((value) => {
                                const isSelected = visibleLines.size > 0 && visibleLines.has(value);
                                const isVisible = shouldShowLine(value);
                                return (
                                    <div
                                        key={value}
                                        onClick={() => handleLegendClick(value)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            opacity: isVisible ? 1 : 0.4
                                        }}
                                    >
                                        <span style={{
                                            width: '10px',
                                            height: '10px',
                                            borderRadius: '50%',
                                            backgroundColor: valueColorMapping[value] || '#666',
                                            marginRight: '8px',
                                            flexShrink: 0
                                        }} />
                                        <span style={{
                                            fontSize: '14px',
                                            fontWeight: isSelected ? 600 : 400,
                                            color: '#374151',
                                            letterSpacing: '0.01em'
                                        }}>
                                            {value}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Chart Area - White background */}
                        <div style={{
                            backgroundColor: '#FFFFFF',
                            borderRadius: '8px',
                            padding: '16px 8px 8px 0',
                            height: '420px'
                        }}>
                            <ResponsiveContainer>
                                <LineChart
                                    data={chartData}
                                    margin={{ top: 10, right: 120, bottom: 20, left: 10 }}
                                    onClick={handleChartClick}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <CartesianGrid
                                        strokeDasharray="none"
                                        stroke="#E5E7EB"
                                        vertical={false}
                                    />
                                    <XAxis
                                        dataKey="date"
                                        axisLine={false}
                                        tickLine={{ stroke: '#E5E7EB', strokeWidth: 1 }}
                                        tick={{ fontSize: 10, fill: '#9CA3AF', letterSpacing: '0.02em' }}
                                        tickMargin={8}
                                        ticks={getEvenlySpacedTicks(chartData)}
                                        tickFormatter={(value) => formatTickDate(value, chartData)}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fontSize: 10, fill: '#9CA3AF', letterSpacing: '0.02em' }}
                                        tickFormatter={(value) => `${value}%`}
                                        domain={[0, maxYValue]}
                                        width={40}
                                    />
                                    <Tooltip content={<CustomTooltip />} />

                                    {/* Year reference lines for long time ranges */}
                                    {createYearReferenceLines(chartData).map((yearLine) => (
                                        <ReferenceLine
                                            key={yearLine.year}
                                            x={yearLine.dataKey}
                                            stroke="#D1D5DB"
                                            strokeWidth={1}
                                            strokeDasharray="4 4"
                                            label={{
                                                value: yearLine.year.toString(),
                                                position: 'top',
                                                style: { fontSize: 9, fill: '#9CA3AF' }
                                            }}
                                        />
                                    ))}

                                    {topValues.map((value, index) => (
                                        <React.Fragment key={value}>
                                            <Line
                                                type="monotone"
                                                dataKey={value}
                                                stroke={valueColorMapping[value] || '#666666'}
                                                strokeWidth={2}
                                                strokeOpacity={shouldShowLine(value) ? 0.85 : 0.15}
                                                dot={false}
                                                activeDot={{ r: 4, strokeWidth: 0 }}
                                                hide={!shouldShowLine(value)}
                                                label={index < topValues.length && chartData.length > 0 ? {
                                                    position: 'right',
                                                    content: (props) => {
                                                        const { x, y, index: dataIndex } = props;
                                                        if (dataIndex !== chartData.length - 1 || !shouldShowLine(value)) return null;

                                                        // Apply collision-avoidance offset
                                                        const offset = computeLabelOffsets[value] || 0;

                                                        return (
                                                            <text
                                                                x={x + 8}
                                                                y={y + offset}
                                                                fill={valueColorMapping[value] || '#666'}
                                                                fontSize={10}
                                                                fontWeight={500}
                                                                dominantBaseline="middle"
                                                            >
                                                                {value.length > 16 ? value.substring(0, 14) + '...' : value}
                                                            </text>
                                                        );
                                                    }
                                                } : false}
                                            />
                                            {showTrendLines && regressionParams[value] && (
                                                <Line
                                                    type="monotone"
                                                    dataKey={`${value}_trend`}
                                                    stroke={valueColorMapping[value] || '#666666'}
                                                    strokeWidth={1.5}
                                                    strokeDasharray="6 4"
                                                    strokeOpacity={shouldShowLine(value) ? 0.5 : 0.1}
                                                    dot={false}
                                                    activeDot={false}
                                                    hide={!shouldShowLine(value)}
                                                />
                                            )}
                                        </React.Fragment>
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    )}

                    {/* Stacked Area Chart - Proportional View */}
                    {chartView === 'proportional' && (
                    <div style={{
                        backgroundColor: '#F7F7F9',
                        borderRadius: '10px',
                        padding: '28px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)'
                    }}>
                        {/* Title Block */}
                        <div style={{ marginBottom: '16px' }}>
                            <h3 style={{
                                fontSize: '24px',
                                fontWeight: 600,
                                color: '#1a1a1a',
                                margin: 0,
                                lineHeight: 1.2
                            }}>
                                {MOTIVATION_TYPES.find(t => t.value === selectedType)?.label || 'Motivation'} Distribution
                            </h3>
                            <p style={{
                                fontSize: '13px',
                                color: '#6B7280',
                                margin: '4px 0 0 0',
                                lineHeight: 1.4
                            }}>
                                n = {totalPostCount.toLocaleString()} posts · {rollingWindowDays}-day rolling relative share
                            </p>
                        </div>

                        {/* Legend - Compact tag list style */}
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '12px 16px',
                            marginBottom: '16px'
                        }}>
                            {topValues.map((value) => (
                                <div
                                    key={value}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    <span style={{
                                        width: '10px',
                                        height: '10px',
                                        borderRadius: '50%',
                                        backgroundColor: valueColorMapping[value] || '#666',
                                        marginRight: '8px',
                                        flexShrink: 0
                                    }} />
                                    <span style={{
                                        fontSize: '14px',
                                        fontWeight: 400,
                                        color: '#374151',
                                        letterSpacing: '0.01em'
                                    }}>
                                        {value}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* Chart Area - White background */}
                        <div style={{
                            backgroundColor: '#FFFFFF',
                            borderRadius: '8px',
                            padding: '16px 8px 8px 0',
                            height: '420px'
                        }}>
                            <ResponsiveContainer>
                                <AreaChart data={stackedChartData} margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
                                    <CartesianGrid
                                        strokeDasharray="none"
                                        stroke="#E5E7EB"
                                        vertical={false}
                                    />
                                    <XAxis
                                        dataKey="date"
                                        axisLine={false}
                                        tickLine={{ stroke: '#E5E7EB', strokeWidth: 1 }}
                                        tick={{ fontSize: 10, fill: '#9CA3AF', letterSpacing: '0.02em' }}
                                        tickMargin={8}
                                        ticks={getEvenlySpacedTicks(stackedChartData)}
                                        tickFormatter={(value) => formatTickDate(value, stackedChartData)}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fontSize: 10, fill: '#9CA3AF', letterSpacing: '0.02em' }}
                                        tickFormatter={(value) => `${Math.round(value)}%`}
                                        domain={[0, 100]}
                                        ticks={[0, 25, 50, 75, 100]}
                                        width={40}
                                    />
                                    <Tooltip
                                        formatter={(value, name) => [`${value.toFixed(1)}%`, name]}
                                        labelFormatter={(label) => `${label}`}
                                        contentStyle={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.96)',
                                            border: '1px solid #E5E7EB',
                                            borderRadius: '6px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                        }}
                                    />

                                    {/* Year reference lines */}
                                    {createYearReferenceLines(stackedChartData).map((yearLine) => (
                                        <ReferenceLine
                                            key={yearLine.year}
                                            x={yearLine.dataKey}
                                            stroke="#D1D5DB"
                                            strokeWidth={1}
                                            strokeDasharray="4 4"
                                            label={{
                                                value: yearLine.year.toString(),
                                                position: 'top',
                                                style: { fontSize: 9, fill: '#9CA3AF' }
                                            }}
                                        />
                                    ))}

                                    {topValues.map((value) => (
                                        <Area
                                            key={value}
                                            type="monotone"
                                            dataKey={value}
                                            stackId="1"
                                            stroke={valueColorMapping[value] || '#666666'}
                                            strokeWidth={1}
                                            fill={valueColorMapping[value] || '#666666'}
                                            fillOpacity={0.7}
                                        />
                                    ))}
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    )}

                    {/* Stacked Bar Chart - Count View */}
                    {chartView === 'count' && (
                    <div style={{
                        backgroundColor: '#F7F7F9',
                        borderRadius: '10px',
                        padding: '28px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)'
                    }}>
                        {/* Title Block */}
                        <div style={{ marginBottom: '16px' }}>
                            <h3 style={{
                                fontSize: '24px',
                                fontWeight: 600,
                                color: '#1a1a1a',
                                margin: 0,
                                lineHeight: 1.2
                            }}>
                                {MOTIVATION_TYPES.find(t => t.value === selectedType)?.label || 'Motivation'} Volume
                            </h3>
                            <p style={{
                                fontSize: '13px',
                                color: '#6B7280',
                                margin: '4px 0 0 0',
                                lineHeight: 1.4
                            }}>
                                n = {totalPostCount.toLocaleString()} posts · {rollingDays === 30 ? 'monthly' : `${rollingDays}-day`} mention counts
                            </p>
                        </div>

                        {/* Legend - Compact tag list style */}
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '12px 16px',
                            marginBottom: '16px'
                        }}>
                            {topValues.map((value) => {
                                const isSelected = visibleLines.size > 0 && visibleLines.has(value);
                                const isVisible = shouldShowLine(value);
                                return (
                                    <div
                                        key={value}
                                        onClick={() => handleLegendClick(value)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            opacity: isVisible ? 1 : 0.4
                                        }}
                                    >
                                        <span style={{
                                            width: '10px',
                                            height: '10px',
                                            borderRadius: '50%',
                                            backgroundColor: valueColorMapping[value] || '#666',
                                            marginRight: '8px',
                                            flexShrink: 0
                                        }} />
                                        <span style={{
                                            fontSize: '14px',
                                            fontWeight: isSelected ? 600 : 400,
                                            color: '#374151',
                                            letterSpacing: '0.01em'
                                        }}>
                                            {value}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Chart Area - White background */}
                        <div style={{
                            backgroundColor: '#FFFFFF',
                            borderRadius: '8px',
                            padding: '16px 8px 8px 0',
                            height: '420px'
                        }}>
                            <ResponsiveContainer>
                                <BarChart data={countChartData} margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
                                    <CartesianGrid
                                        strokeDasharray="none"
                                        stroke="#E5E7EB"
                                        vertical={false}
                                    />
                                    <XAxis
                                        dataKey="date"
                                        axisLine={false}
                                        tickLine={{ stroke: '#E5E7EB', strokeWidth: 1 }}
                                        tick={{ fontSize: 10, fill: '#9CA3AF', letterSpacing: '0.02em' }}
                                        tickMargin={8}
                                        ticks={getEvenlySpacedTicks(countChartData)}
                                        tickFormatter={(value) => formatTickDate(value, countChartData)}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fontSize: 10, fill: '#9CA3AF', letterSpacing: '0.02em' }}
                                        domain={[0, maxCountValue]}
                                        width={45}
                                    />
                                    <Tooltip
                                        formatter={(value, name) => [`${Math.round(value)} mentions`, name]}
                                        labelFormatter={(label) => `${label}`}
                                        contentStyle={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.96)',
                                            border: '1px solid #E5E7EB',
                                            borderRadius: '6px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                        }}
                                    />

                                    {/* Year reference lines */}
                                    {createYearReferenceLines(countChartData).map((yearLine) => (
                                        <ReferenceLine
                                            key={yearLine.year}
                                            x={yearLine.dataKey}
                                            stroke="#D1D5DB"
                                            strokeWidth={1}
                                            strokeDasharray="4 4"
                                            label={{
                                                value: yearLine.year.toString(),
                                                position: 'top',
                                                style: { fontSize: 9, fill: '#9CA3AF' }
                                            }}
                                        />
                                    ))}

                                    {topValues.map((value) => (
                                        <Bar
                                            key={value}
                                            dataKey={value}
                                            stackId="absolute"
                                            fill={valueColorMapping[value] || '#666666'}
                                            stroke="none"
                                            hide={!shouldShowLine(value)}
                                            fillOpacity={shouldShowLine(value) ? 0.85 : 0.2}
                                            radius={[2, 2, 0, 0]}
                                        />
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    )}
                </div>
            ) : (
                <div style={{ 
                    height: '400px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: '#666'
                }}>
                    No data available for the selected criteria
                </div>
            )}

                {/* Posts Loading Indicator */}
                {postsLoading && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '40px',
                        marginTop: '20px'
                    }}>
                        <CircularProgress size={32} />
                        <span style={{ marginLeft: '12px', color: '#6B7280' }}>Loading posts...</span>
                    </div>
                )}

                {/* Posts Display */}
                {!postsLoading && postsReferenced.length > 0 && (
                    <div style={{ marginTop: '20px' }}>
                        <MotivationPostsDisplay
                            posts={postsReferenced}
                            onClose={handleClosePosts}
                            motivationType={MOTIVATION_TYPES.find(t => t.value === selectedType)?.label || 'Motivation'}
                            subtitle={selectedPeriod}
                            autoScroll={true}
                        />
                    </div>
                )}

                <div style={{
                    fontSize: '14px',
                    color: '#666',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '16px',
                    marginTop: '20px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                    <p><strong>About this chart:</strong></p>
                    <p>This time series shows how different {MOTIVATION_TYPES.find(t => t.value === selectedType)?.label.toLowerCase()} change over time for {getSelectedProductName()}.</p>
                    <p>The data shows the top {showTop5Only ? '5' : '10'} most frequent motivations with {rollingWindowDays}-day rolling sums. Each line represents a different motivation category.</p>
                    <p>Data is filtered to only include high-confidence assessments (confidence ≥ 7) and spans the selected time period.</p>
                    <p style={{ marginTop: '8px', fontSize: '12px', color: '#9CA3AF' }}>
                        <em>Click on any point in the chart to view the underlying posts.</em>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SatisfactionReasons; 